




































































































import Vue from "vue";
import draggable from "vuedraggable";
import { v4 as uuidv4 } from "uuid";
import { Picture } from "@/shared/interfaces/Picture.interface";
import iwUploadedPicture from "@/optimizedEncoding/components/buildingBlocks/UploadedPicture.vue";
import iwDropArea from "@/optimizedEncoding/components/buildingBlocks/DropArea.vue";
import { debounce, findIndex, round } from "lodash";
import type { DebouncedFunc } from "lodash";
import iwPictureModal from "@/optimizedEncoding/components/Pictures/PictureModal.vue";
import { pushPageViewEvent } from "@/shared/store/modules/gtm/datalayer";
import { mapState, mapGetters } from "vuex";
import { persistPicturesOrder, uploadPicture } from "@/shared/scripts/UploadPicture";
import iwButtonLoading from "@/shared/components/ButtonLoading.vue";
import iwInputText from "@/optimizedEncoding/components/buildingBlocks/InputText.vue";
import { OptimizedPack } from "@/shared/interfaces/optimizedEncoding/PackSelection.interface";
import { i18n } from "@/shared/scripts/helpers/translation/i18n";

const PICTURE_ORDER_DEBOUNCE_MS = 3000;

export default Vue.extend({

    components: {
        draggable: draggable,
        iwUploadedPicture: iwUploadedPicture,
        iwPictureModal: iwPictureModal,
        iwDropArea: iwDropArea,
        iwButtonLoading: iwButtonLoading,
        iwInputText: iwInputText
    },

    props: {
        fullWidth: {
            type: Boolean,
            default: false,
            required: false,
        },
        isNextSectionVisible: {
            type: Boolean,
            required: false,
            default: false,
        },
    },

    data: function() {
        return {
            errorMessage: "",
            picsProcessing: 0,
            isButtonDisabled: false as boolean,
            validationChecking: false,
            MAX_FILE_SIZE_MB: 10,
            isSavingOrder: false,
            originalPictureOrder: [] as Array<string|undefined>,
            schedulePersistPictureOrder: null as DebouncedFunc<() => void> | null,
        };
    },

    computed: {
        ...mapState("ModalModule", ["isPictureModalOpen"]),
        ...mapState("FormModule", ["currentStep"]),
        ...mapGetters({
            apiErrorMessage: "PicturesModule/getErrorMessage",
            isEditionMode: "FormModule/getIsEditionMode"
        }),

        uploadedImageCount: function(): number {
            return this.uploadedImages.length;
        },

        uploadedImages: {
            get: function(): Array<Picture> {
                return this.$store.getters["PicturesModule/getPictures"];
            },
            set: function(value: Array<Picture>): void {
                this.$store.commit("PicturesModule/setPictures", value);
            }
        },

        getSelectedPackage: function(): string {
            return this.$store.getters["FormModule/getValueTransaction"]("publication.visualizationOption") || "";
        },

        externalVideoUrl: function(): string {
            return this.$store.getters["FormModule/getValue"]("media.externalVideo") || "";
        },

        externalVideoUrlError: function(): string|null {
            return this.$store.getters["FormModule/getErrorObjectByPath"]("media.externalVideo");
        },

        maxImageCount: function(): number {
            const selectedPackType = this.getSelectedPackage;
            const configuredPacks = this.$store.getters["FormModule/getValueTransaction"]("transaction.configuredPacks") || [];
            const selectedPack = configuredPacks.find((pack: OptimizedPack) => pack.packType === selectedPackType);

            return selectedPack?.imageSize || 30;
        },

        maxUploadCount: function(): number {
            const configuredPacks = this.$store.getters["FormModule/getValueTransaction"]("transaction.configuredPacks") || [];
            const highestImageSize = configuredPacks.reduce(
                (max: number, pack: OptimizedPack) => Math.max(max, pack.imageSize || 0),
                0
            );

            return highestImageSize || 100;
        },
        uploadedPicturesMaxSize: function (): number {
            return this.MAX_FILE_SIZE_MB * 1024 * 1024;
        },
        acceptedFormats: function (): Array<string> {
            return [
                "image/jpeg",
                "image/png",
                "image/heic",
            ];
        },
        acceptedFormatsString: function (): string {
            return this.acceptedFormats.join(",");
        },
        isFormValid: function(): boolean {
            // Check if at least one picture is uploaded
            const pictures = this.$store.getters["PicturesModule/getPictures"];
            return pictures && pictures.length > 0 && this.picsProcessing === 0;
        },
        imagineLimitText: function(): string|null {
            const selectedPackType = this.getSelectedPackage;
            if (selectedPackType === "basic") {
                return this.$t("pack_basic_max_images") as string;
            } else if (selectedPackType === "classic") {
                return this.$t("pack_classic_max_images") as string;
            } else {
                return null;
            }
        }
    },

    watch: {
        isPictureModalOpen: function(isOpen) {
            if (isOpen) {
                this.clearErrors();
            }
        },
        uploadedImages: {
            deep: true,
            handler: function() {
                const newIds = this.uploadedImages.map((pic: Picture) => pic.id);
                const sortedNew = [...newIds].sort().join(",");
                const sortedOrig = [...this.originalPictureOrder].sort().join(",");
                if (sortedNew !== sortedOrig) {
                    this.originalPictureOrder = newIds.slice();
                    return;
                }
                if (JSON.stringify(newIds) !== JSON.stringify(this.originalPictureOrder)) {
                    this.schedulePersistPictureOrder?.();
                }
            },
        },
    },

    created: function() {
        this.schedulePersistPictureOrder = debounce(this.runPersistPictureOrder, PICTURE_ORDER_DEBOUNCE_MS);
    },

    mounted: function() {
        this.clearErrors();
        pushPageViewEvent("classified_deposit_v2_step6_pictures");
        this.originalPictureOrder = this.uploadedImages.map(pic => pic.id);
    },

    beforeDestroy: function() {
        this.schedulePersistPictureOrder?.flush();
        this.schedulePersistPictureOrder?.cancel();
    },

    methods: {
        handlePicture: function(file: File) {
            // skip if maximum number of uploaded or pending images is reached
            if (this.uploadedImageCount + this.picsProcessing >= this.maxUploadCount) {
                this.errorMessage = this.$t("maximum_number_of_uploaded_images_is_reached") as string;
                return;
            }

            const parts = file.name.split(".");
            const fileExtension = parts.length > 1 ? parts[parts.length - 1].toLowerCase() : "";
            const normalizedFileType = file.type ||
                (fileExtension === "heic" ? "image/heic" :(fileExtension === "jpg" || fileExtension === "jpeg") ? "image/jpeg" :fileExtension === "png" ? "image/png" : "");

            // skip unsupported file formats
            if (!this.acceptedFormats.includes(normalizedFileType)) {
                this.errorMessage = this.$t("Could_not_upload_file_x_unknown_file_format_x", {
                    filename: file.name,
                    fileformat: normalizedFileType,
                }) as string;
                return;
            }

            // skip images larger than MAX_FILE_SIZE_MB MB
            if (file.size > this.uploadedPicturesMaxSize) {
                this.errorMessage = this.$t("could_not_upload_file_x_size_larger_than_x_mb_x", {
                    filename: file.name,
                    maxSize: this.MAX_FILE_SIZE_MB,
                    /* Dividing the filesize by 1MB to get the size in MB. */
                    filesize: round(file.size /1048576, 2),
                }) as string;
                return;
            }

            this.picsProcessing++;

            // create blob URL for this local image
            const imgUrl = window.URL.createObjectURL(file);

            // set attributes of the picture object
            const imgId = "upload_" + uuidv4();
            let isVertical = false;
            const isHeic = normalizedFileType === "image/heic";

            // HEIC files: skip preview, add picture immediately and upload directly
            if (isHeic) {
                const newPicture: Picture = {
                    id: imgId,
                    url: "", // no preview URL for HEIC
                    isLoading: true,
                    isVertical: false
                };

                this.$store.commit("PicturesModule/addPicture", newPicture);

                uploadPicture(newPicture, file)
                    .then(() => this.picsProcessing--)
                    .catch(err => {
                        this.picsProcessing--;
                        console.error(err);
                    });
                return;
            }

            const img = new Image();
            img.src = imgUrl;

            img.onload = (e) => {
                const target = e.target as HTMLImageElement;

                if (target.height > target.width) {
                    isVertical = true;
                }

                // Create picture object
                const newPicture: Picture = {
                    "id": imgId,
                    "url" : imgUrl,
                    "isLoading": true,
                    "isVertical": isVertical
                };

                // add picture to store
                this.$store.commit("PicturesModule/addPicture", newPicture);

                uploadPicture(newPicture, file)
                    .then(() => this.picsProcessing--)
                    .catch(err => {
                        this.picsProcessing--;
                        console.error(err);
                    });
            };
        },
        moveLeft: function(picture: Picture) {
            const index = findIndex(this.uploadedImages, picture);

            // exit if already first image
            if (index < 1) {
                return;
            }

            // swap pictures positions
            const temp = this.uploadedImages[index - 1];
            Vue.set(this.uploadedImages, index - 1, picture);
            Vue.set(this.uploadedImages, index, temp);

        },
        moveRight: function(picture: Picture) {
            const index = findIndex(this.uploadedImages, picture);

            // exit if already last image
            if (index == this.uploadedImages.length) {
                return;
            }

            // swap pictures positions
            const temp = this.uploadedImages[index + 1];
            Vue.set(this.uploadedImages, index + 1, picture);
            Vue.set(this.uploadedImages, index, temp);
        },
        clearErrors: function() {
            this.errorMessage = "";
            this.$store.commit("PicturesModule/setErrorMessage", "");
        },
        updateExternalVideoUrl: function(event: Event) {
            const target = event.target as HTMLInputElement;
            const value = target.value.trim();

            if (value === "") {
                this.$store.commit("FormModule/deleteValue", "media.externalVideo");
            } else {
                this.$store.dispatch("FormModule/addValue", { path: "media.externalVideo", value: value });
            }
        },
        validateAndContinue: function() {
            if (this.isFormValid) {
                this.$emit("section-completed");
            }
        },
        runPersistPictureOrder: function() {
            this.isSavingOrder = true;
            persistPicturesOrder()
                .then((ok: boolean) => {
                    this.isSavingOrder = false;
                    if (ok) {
                        this.originalPictureOrder = this.uploadedImages.map((pic: Picture) => pic.id);
                    }
                })
                .catch(() => {
                    this.isSavingOrder = false;
                    this.dialog.alert(i18n.t("server_not_responding_error").toString());
                });
        },
    },

});

